"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, TrendingUp, MapPin, ChartBar as BarChart3, Target, ArrowRight, CircleCheck as CheckCircle2, Building2, Sparkles, Award, Layers, ChevronRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
const searchMetrics = [{
  id: "m1",
  label: "Monthly Local Search Volume",
  value: "42,800+",
  change: "+18% YoY",
  description: "Total monthly localized commercial searches across Colchester & North Essex."
}, {
  id: "m2",
  label: "Google Map Pack Dominance",
  value: "3.2x",
  change: "High Intent",
  description: "Local search queries converting directly into calls and physical inquiries."
}, {
  id: "m3",
  label: "Unclaimed Keyword Gap",
  value: "64%",
  change: "Opportunity",
  description: "Niche local search terms currently lacking well-optimized local landing pages."
}, {
  id: "m4",
  label: "Average Conversion Rate",
  value: "8.4%",
  change: "Top Ranking",
  description: "Estimated lead capture rate for Position 1-3 organic local listings."
}];
const colchesterKeywords = [{
  id: "kw1",
  keyword: "SEO consultant Colchester",
  volume: "720/mo",
  difficulty: "Medium",
  intent: "High Commercial",
  opportunity: "Immediate"
}, {
  id: "kw2",
  keyword: "Local SEO agency Essex",
  volume: "1,400/mo",
  difficulty: "High",
  intent: "Transactional",
  opportunity: "Strategic"
}, {
  id: "kw3",
  keyword: "Digital marketing Colchester",
  volume: "1,900/mo",
  difficulty: "High",
  intent: "Commercial",
  opportunity: "Long-term"
}, {
  id: "kw4",
  keyword: "Colchester business search rankings",
  volume: "380/mo",
  difficulty: "Low",
  intent: "High Intent",
  opportunity: "Quick Win"
}, {
  id: "kw5",
  keyword: "Google Maps optimization Colchester",
  volume: "450/mo",
  difficulty: "Medium",
  intent: "Transactional",
  opportunity: "Quick Win"
}];
const opportunitiesList = [{
  id: "op1",
  title: "Google Business Profile Localized Geo-Targeting",
  description: "Optimizing primary categories, Essex-specific service areas, and continuous review management to secure top 3 spot in Colchester Map Packs.",
  impact: "High Map Pack Exposure"
}, {
  id: "op2",
  title: "Colchester Hyper-Local Landing Page Architecture",
  description: "Building tailored landing pages referencing Colchester landmarks, local client case studies, and localized Schema markup.",
  impact: "Boost Organic Traffic"
}, {
  id: "op3",
  title: "Essex Regional Backlink & Citation Building",
  description: "Securing authoritative local press, Essex business directories, and regional partner links to establish true geo-relevance.",
  impact: "Strengthen Domain Trust"
}, {
  id: "op4",
  title: "AI Search & Conversational Query Optimization",
  description: "Structuring content for AI-driven local search queries like 'best local consultant near Colchester' in SearchGPT and Gemini.",
  impact: "Future-Proof Rankings"
}];
const nearbyLocations = [{
  id: "loc1",
  name: "Ipswich",
  county: "Suffolk",
  path: "/LocalSeoConsultantIpswichPage",
  distance: "18 miles"
}, {
  id: "loc2",
  name: "Norwich",
  county: "Norfolk",
  path: "/LocalSeoConsultantNorwichPage",
  distance: "60 miles"
}, {
  id: "loc3",
  name: "Peterborough",
  county: "Cambridgeshire",
  path: "/LocalSeoConsultantPeterboroughPage",
  distance: "78 miles"
}, {
  id: "loc4",
  name: "Lincoln",
  county: "Lincolnshire",
  path: "/LocalSeoConsultantLincolnPage",
  distance: "125 miles"
}, {
  id: "loc5",
  name: "Milton Keynes",
  county: "Buckinghamshire",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "85 miles"
}, {
  id: "loc6",
  name: "Northampton",
  county: "Northamptonshire",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "92 miles"
}];
export default function Colchesterlocalseoanalysis() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const containerStagger = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <section id="colchesterlocalseoanalysis" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider">
            <MapPin className="size-3.5 text-primary" />
            <span>Colchester & East Essex Market Intelligence</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Colchester Local SEO Analysis &amp; Search Trends
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            I continuously monitor local search behavior, map pack competition, and high-intent keyword gaps across Colchester and North Essex. When you work with me, you get tailored strategies engineered specifically for your local target market.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerStagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {searchMetrics.map((metric, i) => <div data-index={i} key={metric.id}>
              <motion.div variants={fadeUp} className="h-full">
                <Card className="h-full bg-card border-border hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardDescription className="text-xs font-medium text-muted-foreground">
                        {metric.label}
                      </CardDescription>
                      <Badge variant="secondary" className="text-[11.5px] px-2 py-0.5">
                        {metric.change}
                      </Badge>
                    </div>
                    <CardTitle className="text-3xl font-bold text-primary font-default">
                      {metric.value}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {metric.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="lg:col-span-7">
            <Card className="bg-card border-border shadow-md overflow-hidden">
              <CardHeader className="bg-muted/50 pb-4 border-b border-border">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Search className="size-5 text-primary" />
                    <CardTitle className="text-xl font-semibold text-foreground font-default">
                      Colchester High-Intent Local Keywords
                    </CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Updated Live Analysis
                  </Badge>
                </div>
                <CardDescription>
                  Search volume and ranking potential for Colchester-focused queries.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-border">
                {colchesterKeywords.map((item, i) => <div data-index={i} key={item.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-muted/30 transition-colors">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground text-sm font-default">
                          {item.keyword}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>Volume: <strong className="text-foreground">{item.volume}</strong></span>
                        <span>•</span>
                        <span>Difficulty: <strong className="text-foreground">{item.difficulty}</strong></span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:justify-end">
                      <Badge variant={item.opportunity === "Quick Win" ? "default" : "secondary"} className="text-xs">
                        {item.opportunity}
                      </Badge>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded cursor-help">
                              {item.intent}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Search intent classification for conversion modeling.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="lg:col-span-5 space-y-6">
            <Card className="bg-primary text-primary-foreground shadow-lg border-none relative overflow-hidden">
              <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
                <Sparkles className="size-32" />
              </div>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Award className="size-5 text-accent" />
                  <CardTitle className="text-xl font-bold font-default text-primary-foreground">
                    My Personal Colchester Strategy
                  </CardTitle>
                </div>
                <CardDescription className="text-primary-foreground/80">
                  Direct hands-on execution by Yassine Essaadi
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm leading-relaxed text-primary-foreground/90">
                <p>
                  Colchester is rapidly growing as a commercial hub in Essex. Many local businesses rely solely on outdated directories or basic SEO templates that fail to capture competitive local search queries.
                </p>
                <p>
                  When you partner with me, you deal directly with an independent specialist. I perform custom technical audits, implement localized schema, and structure content that signals strong geo-relevance to Google.
                </p>
                <Separator className="bg-primary-foreground/20 my-2" />
                <div className="space-y-2 pt-1">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                    <span>No junior account managers — you work 100% directly with me.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                    <span>Tailored Colchester keyword map &amp; competitor gap analysis.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                    <span>Transparent reporting focused on high-quality leads and ROI.</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-sm p-6 text-center space-y-4">
              <h3 className="text-lg font-semibold text-foreground font-default">
                Ready to review your Colchester organic search potential?
              </h3>
              <p className="text-sm text-muted-foreground">
                Book a complimentary 1-on-1 video consultation to review your rankings and strategic opportunities.
              </p>
              <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </Card>
          </motion.div>
        </div>

        <div className="space-y-8 pt-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
              Colchester SEO Growth Opportunities
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              Key technical and content pillars required to dominate organic search results in Colchester.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={containerStagger} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {opportunitiesList.map((op, i) => <div data-index={i} key={op.id}>
                <motion.div variants={fadeUp} className="h-full">
                  <Card className="h-full bg-card border-border hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm flex flex-col justify-between">
                    <CardHeader className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="size-9 rounded-lg bg-secondary flex items-center justify-center text-primary font-bold text-sm">
                          0{i + 1}
                        </div>
                        <Badge variant="outline" className="text-xs border-primary/20 text-primary">
                          {op.impact}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground font-default">
                        {op.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground leading-relaxed">
                      {op.description}
                    </CardContent>
                  </Card>
                </motion.div>
              </div>)}
          </motion.div>
        </div>

        <div className="pt-8 border-t border-border space-y-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Building2 className="size-4" />
                <span>Regional Search Coverage</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
                Local SEO Services in Neighboring Areas
              </h2>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Looking to expand beyond Colchester? Explore local search optimization insights and dedicated consultancy for surrounding cities across East Anglia and East Midlands.
              </p>
            </div>

            <Button asChild variant="outline" size="sm" className="shrink-0">
              <Link to="/ServicesPage">
                <span>All Local SEO Services</span>
                <ChevronRight className="size-4" />
              </Link>
            </Button>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={containerStagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {nearbyLocations.map((loc, i) => <div data-index={i} key={loc.id}>
                <motion.div variants={fadeUp}>
                  <Link to={loc.path} className="group block p-4 rounded-xl bg-card border border-border hover:border-primary hover:shadow-md transition-[border-color,box-shadow,background-color] duration-200">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground group-hover:text-primary transition-colors font-default">
                            {loc.name}
                          </span>
                          <span className="text-xs text-muted-foreground">({loc.county})</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Dedicated local SEO strategy • {loc.distance} from Colchester
                        </p>
                      </div>
                      <ChevronRight className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </Link>
                </motion.div>
              </div>)}
          </motion.div>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="rounded-2xl bg-muted/60 border border-border p-8 sm:p-12 text-center space-y-6 max-w-4xl mx-auto shadow-sm">
          <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Target className="size-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
              Ready to Win More Business in Colchester?
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
              Get an honest assessment of your Colchester SEO opportunities directly from Yassine. No agency fluff or pushy sales reps.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}